import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { Tooltip } from '@clds/tooltip';
import type {} from '@cld/session-service';
import { useAnalytics, useAnalyticsOnce } from '@cld/analytics';
import { NoItemsMessage, WorkspaceMenuItem } from '../../components/WorkspaceMenu';
import { useWorkspaceMenuContext } from '../../context/workspaceMenu/WorkspaceMenu.context';
import { useUnicornApiContext } from '../../UnicornApi.context';

export const useWorkspaceMenuData = () => {
  const isFirstCustomersReceived = useRef(false);
  const [isSingleWorkspace, setIsSingleWorkspace] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 250);
  const { unicornApi } = useUnicornApiContext();
  const { getCustomersList, customersList, currentCustomerObject, setCurrentCustomer } = useWorkspaceMenuContext();
  const currentCustomer = unicornApi?.data.productEnvironment ?? currentCustomerObject;
  const analytics = useAnalytics('product environment menu');
  const analyticsOnce = useAnalyticsOnce('product environment menu');

  useEffect(() => {
    getCustomersList(debouncedSearchTerm);
    if (debouncedSearchTerm) {
      analyticsOnce('search product environment', 'search');
    }
  }, [debouncedSearchTerm, getCustomersList, analyticsOnce]);

  useEffect(() => {
    if (!isFirstCustomersReceived.current && customersList.length) {
      const singleWorkspace = customersList.length === 1;
      isFirstCustomersReceived.current = true;
      setIsSingleWorkspace(singleWorkspace);
    }
  }, [customersList]);

  const onSetCurrentCustomerCallback = useCallback(
    (id: string) => {
      setCurrentCustomer(id);
      if (unicornApi) {
        void unicornApi.methods.changeProductEnvironment(id);
      }
      analytics(`choose a product environment with id: ${id}`);
    },
    [analytics, setCurrentCustomer, unicornApi]
  );
  const items = useMemo(
    () =>
      customersList?.length ? (
        customersList.map((item) => {
          return (
            <Tooltip placement="top" title={item.displayName} key={item.externalId}>
              <WorkspaceMenuItem id={item.externalId} productEnvironment={item.displayName} productEnvironmentStatus={item.status}>
                <Tooltip.OverflowTrigger>{item.displayName}</Tooltip.OverflowTrigger>
              </WorkspaceMenuItem>
            </Tooltip>
          );
        })
      ) : (
        <NoItemsMessage />
      ),
    [customersList]
  );

  return {
    setSearchTerm,
    items,
    currentCustomer,
    setCurrentCustomerId: onSetCurrentCustomerCallback,
    isSingleWorkspace,
    getCustomersList,
  };
};
